import { FormEvent, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AmenitiesSelect from "@/components/AmenitiesSelect/AmenitiesSelect";
import LocationPicker from "@/components/LocationPicker/LocationPicker";
import PlacesSelect from "@/components/PlacesSelect/PlacesSelect";
import PROPERTY_STATUSES from "@/constants/PropertyStatuses";
import PROPERTY_TYPES from "@/constants/PropertyTypes";
import GeoPoint from "@/model/GeoPoint";
import PropertyFormValues from "@/model/PropertyFormValues";
import PropertyStatus from "@/model/PropertyStatus";
import PropertyType from "@/model/PropertyType";
import geocodeAddress from "@/utils/geocodeAddress";
import BOOKING_MODES from "@/constants/BookingModes";
import BookingMode from "@/model/BookingMode";
import "@/components/PropertyForm/PropertyForm.scss";

interface PropertyFormProps {
  title: string;
  initialValues: PropertyFormValues;
  submitLabel: string;
  isSubmitting: boolean;
  error: string | null;
  cancelTo: string;
  /** Status is only editable once the property exists; new listings start LISTED. */
  showStatus?: boolean;
  onSubmit: (values: PropertyFormValues) => void;
  children?: ReactNode;
}

function PropertyForm({
  title,
  initialValues,
  submitLabel,
  isSubmitting,
  error,
  cancelTo,
  showStatus = false,
  onSubmit,
  children,
}: PropertyFormProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<PropertyFormValues>(initialValues);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const setField = <K extends keyof PropertyFormValues>(
    field: K,
    value: PropertyFormValues[K]
  ) => setValues((current) => ({ ...current, [field]: value }));

  const location: GeoPoint | null =
    values.latitude !== null && values.longitude !== null
      ? { latitude: values.latitude, longitude: values.longitude }
      : null;

  const setLocation = (point: GeoPoint) => {
    setLocationError(null);
    setValues((current) => ({
      ...current,
      latitude: point.latitude,
      longitude: point.longitude,
    }));
  };

  const handleLocate = async () => {
    const query = [values.street, values.postalCode, values.city, values.country]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(", ");

    if (!query) {
      setLocationError(t("properties.form.locateEmptyError"));
      return;
    }

    setIsGeocoding(true);
    setLocationError(null);
    try {
      const point = await geocodeAddress(query);
      if (point) {
        setLocation(point);
      } else {
        setLocationError(t("properties.form.locateNotFound"));
      }
    } catch {
      setLocationError(t("properties.form.locateError"));
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!location) {
      setLocationError(t("properties.form.locationRequired"));
      return;
    }
    onSubmit(values);
  };

  return (
    <form className="property-form" onSubmit={handleSubmit}>
      <h1>{title}</h1>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.basics")}</legend>

        <label htmlFor="title">{t("properties.form.titleLabel")}</label>
        <input
          id="title"
          type="text"
          maxLength={120}
          value={values.title}
          onChange={(event) => setField("title", event.target.value)}
          required
        />

        <div className="property-form__row">
          <div className="property-form__field">
            <label htmlFor="propertyType">
              {t("properties.form.propertyTypeLabel")}
            </label>
            <select
              id="propertyType"
              value={values.propertyType}
              onChange={(event) =>
                setField("propertyType", event.target.value as PropertyType)
              }
            >
              {PROPERTY_TYPES.map((option) => (
                <option key={option} value={option}>
                  {t(`properties.type.${option}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="property-form__field">
            <label htmlFor="bookingMode">{t("properties.form.bookingModeLabel")}</label>
            <select
              id="bookingMode"
              value={values.bookingMode}
              onChange={(event) =>
                setField("bookingMode", event.target.value as BookingMode)
              }
            >
              {BOOKING_MODES.map((option) => (
                <option key={option} value={option}>
                  {t(`properties.bookingMode.${option}`)}
                </option>
              ))}
            </select>
          </div>

          {showStatus && (
            <div className="property-form__field">
              <label htmlFor="status">{t("properties.form.statusLabel")}</label>
              <select
                id="status"
                value={values.status}
                onChange={(event) =>
                  setField("status", event.target.value as PropertyStatus)
                }
              >
                {PROPERTY_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {t(`properties.status.${option}`)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <label htmlFor="description">
          {t("properties.form.descriptionLabel")}
        </label>
        <textarea
          id="description"
          maxLength={5000}
          value={values.description}
          onChange={(event) => setField("description", event.target.value)}
        />
      </fieldset>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.location")}</legend>

        <label htmlFor="street">{t("properties.form.streetLabel")}</label>
        <input
          id="street"
          type="text"
          value={values.street}
          onChange={(event) => setField("street", event.target.value)}
          required
        />

        <div className="property-form__row">
          <div className="property-form__field">
            <label htmlFor="postalCode">
              {t("properties.form.postalCodeLabel")}
            </label>
            <input
              id="postalCode"
              type="text"
              value={values.postalCode}
              onChange={(event) => setField("postalCode", event.target.value)}
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="city">{t("properties.form.cityLabel")}</label>
            <input
              id="city"
              type="text"
              value={values.city}
              onChange={(event) => setField("city", event.target.value)}
              required
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="country">{t("properties.form.countryLabel")}</label>
            <input
              id="country"
              type="text"
              value={values.country}
              onChange={(event) => setField("country", event.target.value)}
              required
            />
          </div>
        </div>

        <div className="property-form__locate">
          <button
            type="button"
            className="property-form__secondary-button"
            onClick={handleLocate}
            disabled={isGeocoding}
          >
            {isGeocoding
              ? t("properties.form.locating")
              : t("properties.form.locateButton")}
          </button>
          <span className="property-form__help">
            {t("properties.form.mapHint")}
          </span>
        </div>

        <LocationPicker value={location} onChange={setLocation} />

        {location && (
          <p className="property-form__help">
            {t("properties.form.coordinates", {
              latitude: location.latitude.toFixed(5),
              longitude: location.longitude.toFixed(5),
            })}
          </p>
        )}
        {locationError && (
          <p className="property-form__error">{locationError}</p>
        )}
      </fieldset>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.places")}</legend>
        <p className="property-form__help">{t("properties.form.placesHelp")}</p>
        <PlacesSelect
          value={values.placeIds}
          onChange={(placeIds) => setField("placeIds", placeIds)}
        />
      </fieldset>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.capacity")}</legend>

        <div className="property-form__row">
          <div className="property-form__field">
            <label htmlFor="maxGuests">
              {t("properties.form.maxGuestsLabel")}
            </label>
            <input
              id="maxGuests"
              type="number"
              min={1}
              max={50}
              value={values.maxGuests}
              onChange={(event) => setField("maxGuests", event.target.value)}
              required
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="bedrooms">
              {t("properties.form.bedroomsLabel")}
            </label>
            <input
              id="bedrooms"
              type="number"
              min={0}
              max={50}
              value={values.bedrooms}
              onChange={(event) => setField("bedrooms", event.target.value)}
              required
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="beds">{t("properties.form.bedsLabel")}</label>
            <input
              id="beds"
              type="number"
              min={1}
              max={100}
              value={values.beds}
              onChange={(event) => setField("beds", event.target.value)}
              required
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="bathrooms">
              {t("properties.form.bathroomsLabel")}
            </label>
            <input
              id="bathrooms"
              type="number"
              min={0}
              max={50}
              value={values.bathrooms}
              onChange={(event) => setField("bathrooms", event.target.value)}
              required
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="surfaceM2">
              {t("properties.form.surfaceM2Label")}
            </label>
            <input
              id="surfaceM2"
              type="number"
              min={1}
              value={values.surfaceM2}
              onChange={(event) => setField("surfaceM2", event.target.value)}
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.amenities")}</legend>
        <AmenitiesSelect
          value={values.amenities}
          onChange={(amenities) => setField("amenities", amenities)}
        />
      </fieldset>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.stay")}</legend>

        <div className="property-form__row">
          <div className="property-form__field">
            <label htmlFor="checkInTime">
              {t("properties.form.checkInTimeLabel")}
            </label>
            <input
              id="checkInTime"
              type="time"
              value={values.checkInTime}
              onChange={(event) => setField("checkInTime", event.target.value)}
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="checkOutTime">
              {t("properties.form.checkOutTimeLabel")}
            </label>
            <input
              id="checkOutTime"
              type="time"
              value={values.checkOutTime}
              onChange={(event) => setField("checkOutTime", event.target.value)}
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="minNights">
              {t("properties.form.minNightsLabel")}
            </label>
            <input
              id="minNights"
              type="number"
              min={1}
              value={values.minNights}
              onChange={(event) => setField("minNights", event.target.value)}
            />
          </div>
        </div>

        <label htmlFor="houseRules">{t("properties.form.houseRulesLabel")}</label>
        <textarea
          id="houseRules"
          maxLength={2000}
          value={values.houseRules}
          onChange={(event) => setField("houseRules", event.target.value)}
        />
      </fieldset>

      <fieldset className="property-form__section">
        <legend>{t("properties.form.sections.pricing")}</legend>

        <div className="property-form__row">
          <div className="property-form__field">
            <label htmlFor="nightlyPriceAmount">
              {t("properties.form.nightlyPriceLabel")}
            </label>
            <input
              id="nightlyPriceAmount"
              type="number"
              min={0.01}
              step="0.01"
              value={values.nightlyPriceAmount}
              onChange={(event) =>
                setField("nightlyPriceAmount", event.target.value)
              }
              required
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="cleaningFeeAmount">
              {t("properties.form.cleaningFeeLabel")}
            </label>
            <input
              id="cleaningFeeAmount"
              type="number"
              min={0}
              step="0.01"
              value={values.cleaningFeeAmount}
              onChange={(event) =>
                setField("cleaningFeeAmount", event.target.value)
              }
            />
          </div>
          <div className="property-form__field">
            <label htmlFor="priceCurrency">
              {t("properties.form.currencyLabel")}
            </label>
            <input
              id="priceCurrency"
              type="text"
              maxLength={3}
              value={values.priceCurrency}
              onChange={(event) =>
                setField("priceCurrency", event.target.value.toUpperCase())
              }
            />
          </div>
        </div>
      </fieldset>

      {children}

      {error && <p className="property-form__error">{error}</p>}

      <div className="property-form__actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("properties.form.submitting") : submitLabel}
        </button>
        <Link to={cancelTo}>{t("properties.form.cancel")}</Link>
      </div>
    </form>
  );
}

export default PropertyForm;
export type { PropertyFormProps };
